import { useEffect, useState } from "react";
import styled from "styled-components";
import Image from "../../components/controls/image";
import LoadingSpinner from "../../components/controls/loading-spinner";
import TableView, { ColumnConfig } from "../../components/controls/table-view";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { fetchPriceHistory } from "../../store/features/ordering/approval/approval-slice";
import { ApplicationProductCostPriceHistory } from "../../models/product/application-product-cost-price-history";
import { currencyFormatter } from "../../helpers/formatters";
import { FeatureCard } from "../../styles/shared/card";
import { MainContent } from "../../styles/shared/drawer";

interface PriceHistoryDrawerProps {
    productId: number;
    branchId: number;
}

export default function PriceHistoryDrawer({ productId, branchId }: PriceHistoryDrawerProps) {
    const dispatch = useAppDispatch();
    const [priceHistory, setPriceHistory] = useState<ApplicationProductCostPriceHistory>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (productId) {
            setLoading(true);
            dispatch(fetchPriceHistory({ productId, branchId })).then((response) => {
                const result = response.payload as ApplicationProductCostPriceHistory;
                if (result) {
                    setPriceHistory(result);
                }
                setLoading(false);
            });
        }
    }, [productId, branchId, dispatch]);

    const columnConfig: { [key: string]: ColumnConfig } = {
        id: {
            hidden: true
        },
        orderDate: {
            order: 1,
            name: "Order Date",
            render: (value: Date) => new Date(value).toLocaleDateString("en-gb", {
                year: "numeric",
                month: "short",
                day: "numeric",
            }),
        },
        orderQuantity: {
            order: 2,
            name: "Qty",
        },
        costPrice: {
            order: 3,
            name: "Cost",
            render: (value: number, row: any) => (
                <>
                    <div>{currencyFormatter.format(value)}</div>
                    <div>{currencyFormatter.format(row.unitPrice)} per {row.sizeUnit}</div>
                </>
            ),
        },
        size: {
            order: 4,
            render: (value: number, row: any) => (
                <div>{value} {row.sizeUnit}</div>
            ),
        },

        branch: {
            hidden: true,
        },
        supplierName: {
            order: 5,
            name: "Supplier",
        },



        unitPrice: {
            hidden: true
        },
        sizeUnit: {
            hidden: true
        }
    };

    if (loading) {
        return (
            <MainContent>
                <LoadingSpinner />
            </MainContent>
        );
    }

    if (!priceHistory) {
        return <></>;
    }

    const rows = priceHistory.priceHistory.map((line, index) => ({
        id: index,
        ...line,
    }));

    const count = rows.length;
    const averageQuantity = count > 0 ? rows.reduce((sum, r) => sum + r.orderQuantity, 0) / count : 0;
    const averageCost = count > 0 ? rows.reduce((sum, r) => sum + r.costPrice, 0) / count : 0;
    const averageTotalCost = count > 0 ? rows.reduce((sum, r) => sum + r.costPrice * r.orderQuantity, 0) / count : 0;

    return (
        <MainContent>
            <ProductHeader>
                <ProductImage>
                    <Image image={priceHistory.image}></Image>
                </ProductImage>
                <ProductHeaderText>
                    <ProductName>{priceHistory.name}</ProductName>
                    {count > 0 && (
                        <Averages>
                            <div><AverageLabel>Avg Qty:</AverageLabel> {averageQuantity.toFixed(2)}</div>
                            <div><AverageLabel>Avg Cost:</AverageLabel> {currencyFormatter.format(averageCost)}</div>
                            <div><AverageLabel>Avg Total Cost:</AverageLabel> {currencyFormatter.format(averageTotalCost)}</div>
                        </Averages>
                    )}
                </ProductHeaderText>
            </ProductHeader>
            <FeatureCard>
                <TableView
                    id="tblPriceHistory"
                    columnConfig={columnConfig}
                    data={rows}
                    totalCount={rows.length}
                    emptyText="No price history found"
                    preLoadedData
                    showHeaders
                    search={false}
                />
            </FeatureCard>
        </MainContent>
    );
}

const ProductHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    padding: 10px;
    border-bottom: 3px solid ${(props) => props.theme.application.scheme.primary};
    margin-bottom: 10px;
`;

const ProductImage = styled.div`
    img {
        width: 80px;
        height: 80px;
    }
`;

const ProductName = styled.div`
    font-size: 1.2rem;
    font-weight: 600;
`;

const ProductHeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
`;

const Averages = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 0.85rem;
    color: ${(props) => props.theme.application.scheme.neutral ?? "inherit"};
`;

const AverageLabel = styled.span`
    font-weight: 600;
`;
